import React from 'react';
import type { Candidate } from '@axiom/validation';
import {
  Button,
  Card,
  CardHeader,
  CardSection,
  CondensedLarge,
  Flashy,
  Form,
  FormGroup,
  FormLabel,
  Gutter,
  Layout,
  Radio,
  SmallHeader,
  useApi,
} from '@axiom/ui';
import { CandidatesConst } from '@axiom/const';

import { CandidateContactInformationApi } from '../../api/candidate-contact-information-api';
import { EnvUtil } from '../../utils/env-util';
import { CandidateUtil } from '../../utils/candidate-util';

import { PreferencesContactInformationFormSchema } from './preferences-contact-information-form-schema';

const { WorkingEmploymentStatuses } = CandidatesConst;

export type PreferencesContactInformationProps = {
  name?: string;
  candidate: Candidate;
};

export const PreferencesContactInformation = ({
  name = 'PREFERENCES_CONTACT_INFORMATION',
  candidate,
}: PreferencesContactInformationProps) => {
  const [{ data: contactInformation }] = useApi(
    CandidateContactInformationApi.readContactInformation(candidate.id)
  );

  const {
    preferredContactMethod,
    workEmail,
    personalEmail,
    employmentStatus,
    mobilePhone,
    homePhone,
  } = contactInformation;

  const showEditInMyApps = WorkingEmploymentStatuses.includes(employmentStatus);
  const talentPartnerUser = CandidateUtil.getTalentPartnerUser(candidate);
  const updatePhoneSubject = 'Please update my contact information';
  const updatePhoneBody = `Dear ${talentPartnerUser.firstName},%0D%0A%0D%0APlease update my contact information in Greenhouse to the following:`;
  const isInactiveStatus = !showEditInMyApps;

  const workEmailFormatted = isInactiveStatus ? (
    <>
      {workEmail} <Flashy italic>(disabled)</Flashy>
    </>
  ) : (
    workEmail
  );

  return (
    <Card name={name}>
      <CardHeader>
        <SmallHeader>Contact Information</SmallHeader>
      </CardHeader>
      <CardSection divider>
        <Form
          name={`${name}_FORM`}
          schema={PreferencesContactInformationFormSchema}
          onSubmit={async (_formData, _actions, values) => {
            await CandidateContactInformationApi.updateContactInformation(
              candidate.id,
              values
            );
            await CandidateContactInformationApi.refreshContactInformation(
              candidate.id
            );
          }}
          initialValues={{
            preferredContactMethod,
          }}
          submitOnChange
        >
          {() => (
            <FormGroup
              name="preferredContactMethod"
              label="Preferred Email"
              description="Where would you like us to send notifications about new opportunities or things that need your attention?"
            >
              <Layout direction="vertical" verticalGutter="8px">
                {!!workEmail && (
                  <Radio
                    name="preferredContactMethod"
                    option="Axiom"
                    displayValue={workEmailFormatted}
                  />
                )}
                {!!personalEmail && (
                  <Radio
                    name="preferredContactMethod"
                    option="Personal"
                    displayValue={personalEmail}
                  />
                )}
                {!!workEmail && !!personalEmail && (
                  <Radio
                    name="preferredContactMethod"
                    option="Both"
                    displayValue="Both"
                  />
                )}
                {!workEmail && !personalEmail && (
                  <Flashy name="NO_EMAIL_WARNING" bold color="textAlert">
                    Please reach out to your Axiom contact to set your email.
                  </Flashy>
                )}
                {preferredContactMethod === 'Axiom' ? (
                  <Gutter top="16px">
                    <Flashy italic>
                      <CondensedLarge name="REVERT_EMAIL_MESSAGE">
                        We will revert to your personal email if no other email
                        is available.
                      </CondensedLarge>
                    </Flashy>
                  </Gutter>
                ) : null}
              </Layout>
            </FormGroup>
          )}
        </Form>
      </CardSection>
      <CardSection divider>
        <FormLabel>Phone Number</FormLabel>
        <Gutter bottom="8px" />
        <CondensedLarge name="mobilePhone">
          Mobile: {mobilePhone ?? '--'}
        </CondensedLarge>
        <Gutter bottom="8px" />
        <CondensedLarge name="homePhone">
          Telephone: {homePhone ?? '--'}
        </CondensedLarge>
      </CardSection>
      <CardSection divider>
        {showEditInMyApps ? (
          <>
            <CondensedLarge>
              Update your contact information in Workday
            </CondensedLarge>
            <Gutter bottom="8px" />
            <Button
              name="MYAPPS_BUTTON"
              pattern="secondary"
              variation="outline"
              toTab={EnvUtil.myAppsUrl}
            >
              Launch My Apps
            </Button>
          </>
        ) : (
          <CondensedLarge>
            To update your contact information, reach out to your{` `}
            <a
              data-test="REACHOUT_LINK"
              href={`mailto:${talentPartnerUser.email}?subject=${updatePhoneSubject}&body=${updatePhoneBody}`}
            >
              talent partner.
            </a>
          </CondensedLarge>
        )}
      </CardSection>
    </Card>
  );
};
